import { each, filter, find, findLast, groupBy, includes, sortBy, sumBy, without } from '@dt/fe-utils/dist/lodash'
import { makeAutoObservable, autorun, action } from 'mobx'
import { toDecimal } from '@dt/fe-utils'
import { Console, cartMobx } from 'restaurant'
import { memberMobx, thirdPartyMobx, tipMobx } from '@mobxStore'
import payChannelMobx from './payChannel.mobx'
import bankCardMobx from './bankCard.mobx'

const console = new Console('console.payplan')

interface IPayingOrder {
  type: 'dining';
  uniqueKey: string | 'temp';
  diningOption: TEatType;
  subtotal: number;
  tax: number;
  extraFee: number;
  isCache?: boolean;
}

class PayPlan {
  constructor () {
    makeAutoObservable(this, {
      calc: action,
      addCacheGiftCardShowList: action
    })
  }
  cacheOrder?: IPayingOrder
  cacheMemberCard?: IMemberCardInfo
  cacheThirdCoupons?: IThirdCouponPrePay[] = []
  cacheGiftCardList: IMemberGiftCardInfo[]
  cacheGiftCardShowList: IMemberGiftCardInfo[] = []
  shouldCalc?: boolean

  isTipUserSelected?: boolean // 用户是否手动选择了小费
  // userMemberCardPayAmount?: number // 用户手动输入的会员卡支付金额
  // userGiftCardCardPayAmount: Record<string, number> = {} // 用户手动输入的礼品卡支付金额
  userSelectedExternalType?: TPayMethodType // 用户选择的三方支付方式
  userChangedPlan?: boolean // 用户是否手动修改过方案

  selectedTipOption?: TTipOption
  payMethods: TPayMethod[] = []
  payAndRecharge: TPayMethod[] = []
  isTipShow: {
    label: string | '';
    isShowTip: boolean | false;
  }

  isForceTemp: boolean = false

  showAllGiftCardList: boolean = false

  get orderAmount (): number | undefined {
    if (this.cacheOrder) {
      const { subtotal, tax, extraFee } = this.cacheOrder
      return subtotal + tax + extraFee
    } else {
      return 0
    }
  }

  get tipAmount (): number {
    if (this.selectedTipOption) {
      return this.selectedTipOption.amount
    } else {
      return 0
    }
  }

  get totalAmount (): number {
    return this.orderAmount + this.tipAmount
  }

  get totalAmountOffThirdCouponPrice (): number {
    return this.orderAmount + this.tipAmount - thirdPartyMobx.couponResult.offPrice
  }

  get memberCardMethod (): IMemberCardPayMethod | undefined {
    return find(this.payMethods, x => x.type === 'MemberCard') as IMemberCardPayMethod | undefined
  }

  get giftCardMethodList (): IGiftCardPayMethod[] {
    return filter(this.payMethods, x => x.type === 'GiftCard') as IGiftCardPayMethod[]
  }

  get externalMethod (): TPayMethod | undefined {
    return find(this.payMethods, x => x.type !== 'ThirdCouponPay' && x.type !== 'GiftCard' && x.type !== 'MemberCard')
  }

  get thirdCouponMethod (): IThirdCouponPayMethod[] {
    return filter(this.payMethods, x => x.type === 'ThirdCouponPay') as IThirdCouponPayMethod[]
  }

  get canPay (): boolean {
    const total = sumBy(this.payMethods, x => x.amount)
    return toDecimal(this.totalAmount, 2) === toDecimal(total, 2)
  }

  beginCalc () {
    this.shouldCalc = true
  }

  endCalc () {
    this.shouldCalc = false
  }

  clear () {
    this.isTipUserSelected = false
    this.selectedTipOption = undefined
    // this.userMemberCardPayAmount = undefined
    // this.userGiftCardCardPayAmount = {}
    this.userSelectedExternalType = undefined
    this.userChangedPlan = false
    this.payMethods = []
  }

  setUserChangedPlan (isDo: boolean) {
    this.userChangedPlan = isDo
  }

  build () {
    console.toJS('开始计算支付方案', this.cacheOrder, this.cacheMemberCard, this.cacheGiftCardList, '关于银行卡List', bankCardMobx.memberBankCards)

    const newPayMethods: TPayMethod[] = [...this._buildThirdCoupon(this.cacheThirdCoupons, [])]
    if (this.userChangedPlan) {
      console.toJS('用户手动修改过方案，恢复原有方案')
      if (this.memberCardMethod && this.cacheMemberCard) {
        // 原有会员卡金额
        let { amount, tip } = this.memberCardMethod

        // 不能支付小费
        if (!this.cacheMemberCard.card.isPayTip) {
          amount -= tip
          tip = 0
        }

        // 新的会员卡余额可能比之前金额小
        amount = Math.min(amount, this.cacheMemberCard.balance)
        tip = Math.min(amount, tip)

        newPayMethods.push({
          type: 'MemberCard',
          channel: 'MemberBalance',
          memberCard: this.cacheMemberCard,
          amount,
          tip,
        })
      }
      if (this.giftCardMethodList) {
        newPayMethods.push(...this.giftCardMethodList)
      }
      this._buildExternal(newPayMethods, this.userSelectedExternalType)
    } else {
      // 会员卡
      this._buildMemberCard(this.cacheMemberCard, newPayMethods)
      // 礼品卡
      each(this.cacheGiftCardList, x => this._buildGiftCard(x, newPayMethods))
      // 三方支付
      this._buildExternal(newPayMethods)
    }

    this._setPayMethods(newPayMethods, [])
  }

  _buildThirdCoupon (coupons: IThirdCouponPrePay[], methodList: TPayMethod[]) {
    console.log('构建三方券', coupons)
    if (!coupons.length) return []

    const groupByCoupon = groupBy(coupons, 'channel')

    const methods: IThirdCouponPayMethod[] = []
    for (let channel in groupByCoupon) {
      const list = groupByCoupon[channel]
      list.forEach((item: any) => {
        methods.push({
          type: 'ThirdCouponPay',
          channel: channel as ThirdCouponChannel,
          channelExt: {
            couponCode: item.encryptedCode,
            couponAmount: item.price,
            couponPayAmount: item.couponPayAmount,
            couponPrepareCode: item.couponCode
          },
          amount: item.price,
          tip: 0,
        })
      })
    }

    methodList = [...methods]
    return methodList
  }

  _buildMemberCard (memberCard: IMemberCardInfo, methodList: TPayMethod[]) {
    const remainOrderAmount = Math.max(this.orderAmount - sumBy(methodList, x => x.amount - x.tip), 0)
    const remainTipAmount = Math.max(this.tipAmount - sumBy(methodList, x => x.tip), 0)
    const remainTotalAmount = remainOrderAmount + remainTipAmount

    if (!payChannelMobx.availableMemberCardMethod || remainTotalAmount <= 0 || !memberCard) return

    let amount = Math.min(remainTotalAmount, memberCard.balance)
    let tip = Math.min(amount, remainTipAmount)

    // 不能支付小费
    if (!memberCard.card.isPayTip) {
      amount = Math.min(remainOrderAmount, memberCard.balance)
      tip = 0
    } else {
      tip = Math.min(remainTipAmount, memberCard.balanceRecharge)
      amount = Math.min(remainOrderAmount + tip, memberCard.balance)
    }

    methodList.push({
      type: 'MemberCard',
      channel: 'MemberBalance',
      memberCard,
      amount,
      tip,
    })
  }

  _buildGiftCard (giftCard: IMemberGiftCardInfo, methodList: TPayMethod[]) {
    const remainOrderAmount = Math.max(this.orderAmount - sumBy(methodList, x => x.amount - x.tip), 0)
    const remainTipAmount = Math.max(this.tipAmount - sumBy(methodList, x => x.tip), 0)
    const remainTotalAmount = remainOrderAmount + remainTipAmount
    if (!payChannelMobx.availableGiftCardMethod || remainTotalAmount <= 0 || !giftCard || (!giftCard.giftcard.allowAgainLoad && giftCard.balance <= 0)) return

    let amount = Math.min(remainTotalAmount, giftCard.balance)
    let tipAmount = Math.min(remainTotalAmount, giftCard.balance - giftCard.balanceGive)
    let tip = Math.min(tipAmount, remainTipAmount)

    // 不能支付小费
    if (!giftCard.giftcard.isPayTip) {
      amount = Math.min(remainOrderAmount, giftCard.balance)
      tip = 0
    }

    methodList.push({
      type: 'GiftCard',
      channel: 'GiftCard',
      giftCard,
      amount,
      tip,
    })
  }

  _buildExternal (methodList: TPayMethod[], preferType?: TPayMethodType) {
    const remainOrderAmount = Math.max(this.orderAmount - sumBy(methodList, x => x.amount - x.tip), 0)
    const remainTipAmount = Math.max(this.tipAmount - sumBy(methodList, x => x.tip), 0)
    const remainTotalAmount = remainOrderAmount + remainTipAmount
    if (remainTotalAmount <= 0 || payChannelMobx.availableExternalMethods.length === 0) return
    let methodType = payChannelMobx.availableExternalMethods[0]
    if (preferType && includes(payChannelMobx.availableExternalMethods, preferType)) {
      methodType = preferType
    }
    if (methodType === 'BankCardOnline') {
      const channel = payChannelMobx.methodChannel.BankCardOnline?.channelType
      let tokenType: 'multi' | 'single' = 'multi'
      if (channel === 'HeartLandBankCard') {
        tokenType = (payChannelMobx.channelAccount.HeartLandBankCard?.tokenType || 'single') as 'multi' | 'single'
      }
      let mutCard: IMutBankCardInfo | undefined
      if (tokenType === 'multi' && bankCardMobx.memberBankCards.length > 0) {
        mutCard = bankCardMobx.memberBankCards[0]
      }
      methodList.push({
        type: 'BankCardOnline',
        channel,
        bankCardInfo: {
          tokenType,
          creditCardType: mutCard?.creditCardType,
          creditCardNo: mutCard?.creditCardNo,
          creditCardNumber: mutCard?.creditCardNumber,
        },
        amount: remainTotalAmount,
        tip: remainTipAmount,
      })
    } else if (methodType === 'ApplePayWeb') {
      const channel = payChannelMobx.methodChannel.ApplePayWeb?.channelType
      if (channel === 'StripeApplePay' || channel === 'CardConnectApplePay' || channel === 'GlobalPaymentsApplePay') {
        methodList.push({
          type: 'ApplePayWeb',
          channel,
          amount: remainTotalAmount,
          tip: remainTipAmount,
        })
      }
    } else if (methodType === 'PayPalWeb') {
      const channel = payChannelMobx.methodChannel.PayPalWeb?.channelType
      if (channel === 'PayPalWeb') {
        methodList.push({
          type: 'PayPalWeb',
          channel,
          amount: remainTotalAmount,
          tip: remainTipAmount,
        })
      }
    } else if (methodType === 'AlipayApp') {
      const channel = payChannelMobx.methodChannel.AlipayApp?.channelType
      if (['AlipayApp', 'ShengPayAlipayWeb', 'ShengPayAlipayWebV2'].includes(channel)) {
        methodList.push({
          type: 'AlipayApp',
          channel,
          amount: remainTotalAmount,
          tip: remainTipAmount,
        } as any)
      }
    } else if (methodType === 'WechatPayWeb') {
      const channel = payChannelMobx.methodChannel.WechatPayWeb?.channelType
      if (['WechatPayWeb', 'ShengPayWechatWeb', 'ShengPayWechatWebV2'].includes(channel)) {
        methodList.push({
          type: 'WechatPayWeb',
          channel,
          amount: remainTotalAmount,
          tip: remainTipAmount,
        } as any)
      }
    }
  }

  setTipStatus (label: string, isShowTip: boolean) {
    this.isTipShow.label = label
    this.isTipShow.isShowTip = isShowTip
  }

  _setPayMethods (newPayMethods: TPayMethod[], immutablePayMethods: TPayMethod[]) {
    console.toJS('处理前支付方案', ...newPayMethods)

    each(newPayMethods, x => {
      x.amount = toDecimal(x.amount, 2) as any
      x.tip = toDecimal(x.tip, 2) as any
    })

    // 处理金额溢出的情况
    const orderTotal = sumBy(newPayMethods, x => (x.amount - x.tip))
    const tipTotal = sumBy(newPayMethods, x => x.tip)
    let orderOverflow = toDecimal(orderTotal - this.orderAmount, 2) as any
    let tipOverflow = toDecimal(tipTotal - this.tipAmount, 2) as any

    console.toJS('溢出金额', orderOverflow, tipOverflow)
    findLast(newPayMethods, x => {
      if ((orderOverflow > Number.EPSILON || tipOverflow > Number.EPSILON)) {
        const immutable = includes(immutablePayMethods, x)
        console.toJS('可更改？', x, immutable)

        if (!immutable) {
          const orderAmount = toDecimal(x.amount - x.tip, 2) as any
          const tipAmount = x.tip
          const orderDeductAmount = Math.min(orderOverflow, orderAmount)
          const tipDeductAmount = Math.min(tipOverflow, tipAmount)

          console.toJS('扣除溢出金额', x, orderDeductAmount, tipDeductAmount)

          const newOrderAmount = toDecimal(orderAmount - orderDeductAmount, 2) as any
          const newTipAmount = toDecimal(tipAmount - tipDeductAmount, 2) as any
          x.amount = toDecimal(newOrderAmount + newTipAmount, 2) as any
          x.tip = newTipAmount
          orderOverflow = toDecimal(orderOverflow - orderDeductAmount, 2) as any
          tipOverflow = toDecimal(tipOverflow - tipDeductAmount, 2) as any
        }
      } else {
        return true
      }
    })

    // 过滤掉除会员卡外，付款金额为0的方式
    const methods = filter(newPayMethods, x => x.amount > Number.EPSILON || x.type === 'MemberCard')
    // const methods = bo === 'cn' ? filter(newPayMethods, x => x.amount > Number.EPSILON) : filter(newPayMethods, x => x.amount > Number.EPSILON || x.type === 'MemberCard')
    this.payMethods = methods

    console.toJS('处理后支付方案', this.payMethods, payPlanMobx.tipAmount, '小费')
    // 判断银行卡是否绑定
    if (bankCardMobx.memberBankCards.length < 1) {
      let noTip: any = {}
      this.payMethods.map((item) => {
        noTip[`${item.type}`] = item.tip
      })
      console.log('关于小费的list', noTip, noTip.GiftCard)
      if (noTip?.GiftCard === 0 && noTip?.MemberCard === 0) {
        // this.setTipStatus(i18n.t('order.cardAndGiftcard'), true)
      } else if (noTip?.MemberCard === 0) {
        // this.setTipStatus(i18n.t('order.card'), true)
      } else if (noTip?.GiftCard === 0) {
        // this.setTipStatus(i18n.t('order.giftcard'), true)
      }
    }
  }

  calcTip (order: IPayingOrder) {
    const { diningOption, subtotal, tax } = order
    const options = tipMobx.loadOptions(diningOption, subtotal, tax)
    if (!options || options.length === 0) {
      this.selectedTipOption = undefined
      this.isTipUserSelected = undefined
    } else {
      if (!this.selectedTipOption || !this.isTipUserSelected) {
        this.selectedTipOption = options[0]
      } else {
        let newOption: TTipOption | undefined
        if (this.selectedTipOption.type === 'percent') {
          const previous = this.selectedTipOption as IPercentTipOption
          newOption = find(options, x => x.type === 'percent' && x.percent === previous.percent)
        } else if (this.selectedTipOption.type === 'amount') {
          const previous = this.selectedTipOption as IAmountTipOption
          newOption = find(options, x => x.type === 'amount' && x.amount === previous.amount)
        } else if (this.selectedTipOption.type === 'noTip') {
          newOption = find(options, x => x.type === 'noTip')
        } else if (this.selectedTipOption.type === 'custom') {
          const previous = this.selectedTipOption as ICustomTipOption
          newOption = find(options, x => x.type === 'custom')
          newOption.amount = previous.amount
        }
        this.selectedTipOption = newOption || options[0]
      }
    }
  }

  calc (order: IPayingOrder, memberCard: IMemberCardInfo | undefined, giftCardList: IMemberGiftCardInfo[], thirdCouponList: IThirdCouponPrePay[]) {
    if (!order) return

    if (!this.cacheOrder) {
      this.clear()
      this.calcTip(order)
    } else {
      const sameOrder = order.type === this.cacheOrder.type &&
        order.uniqueKey === this.cacheOrder.uniqueKey
      const sameProps = toDecimal(order.subtotal, 2) === toDecimal(this.cacheOrder.subtotal, 2) &&
        toDecimal(order.tax, 2) === toDecimal(this.cacheOrder.tax, 2) &&
        toDecimal(order.extraFee, 2) === toDecimal(this.cacheOrder.extraFee, 2) &&
        order.diningOption === this.cacheOrder.diningOption

      console.toJS('订单是否相同：', sameOrder, order.uniqueKey, this.cacheOrder.uniqueKey)
      console.toJS('属性是否相同：', sameProps, order, this.cacheOrder)
      if (!order.isCache) {
        if (!sameOrder && this.cacheOrder?.uniqueKey !== 'temp') {
          console.toJS('不同订单，清空原有方案')
          this.clear()
          this.calcTip(order)
        } else if (!sameProps) {
          this.calcTip(order)
        }
      }
    }

    this.cacheThirdCoupons = thirdCouponList
    this.cacheOrder = order
    this.cacheMemberCard = memberCard
    this.cacheGiftCardList = sortBy(giftCardList, x => x.balance)

    this.build()
  }

  selectTipOption (option: TTipOption) {
    this.selectedTipOption = option
    this.isTipUserSelected = true
    this.calc(this.cacheOrder, this.cacheMemberCard, this.cacheGiftCardList, this.cacheThirdCoupons)
  }

  changeExternalPayMethod (method: TPayMethodType) {
    if (!this.externalMethod) {
      // 三方券需要常驻
      const newPayMethods = [...this._buildThirdCoupon(this.cacheThirdCoupons, [])]
      this._buildExternal(newPayMethods, method)
      const payMethods = filter(this.payMethods, x => x.type === 'MemberCard' || x.type === 'GiftCard').map(x => {
        x.amount = 0
        x.tip = 0
        return x
      })
      newPayMethods.unshift(...payMethods)
      this.payMethods = newPayMethods
    }
    this.externalMethod.type = method
    this.externalMethod.channel = payChannelMobx.methodChannel[method]?.channelType
    this.userSelectedExternalType = method
    this.userChangedPlan = true
    if (method === 'BankCardOnline') {
      const newPayMethods = [...filter(this.payMethods, x => x.type === 'ThirdCouponPay'), ...filter(this.payMethods, x => x.type === 'MemberCard' || x.type === 'GiftCard')]
      // 重新计算三方支付的方案
      this._buildExternal(newPayMethods, 'BankCardOnline')
      const payMethod = find(this.payMethods, x => x.type === 'MemberCard')
      this._setPayMethods(newPayMethods, [payMethod])
    }
  }

  setBankCardInfo (card: TBankCardInfo) {
    const method = find(this.payMethods, x => x.type === 'BankCardOnline')
    if (method && method.type === 'BankCardOnline') {
      console.toJS('setBankCardInfo', card)
      method.bankCardInfo = card
    }
  }

  setMemberCardPayAmount (amount: number) {
    const method = find(this.payMethods, x => x.type === 'MemberCard')
    if (!method) return
    // this.userMemberCardPayAmount = amount
    this.userChangedPlan = true

    method.amount = amount
    // 新的金额不足以包含原有的小费金额
    if (method.tip > amount) {
      method.tip = amount
    }

    // 尽可能吸收其它方式支付的小费到当前支付方式
    if (this.cacheMemberCard.card.isPayTip) {
      method.tip = Math.min(this.tipAmount, amount, this.cacheMemberCard.balanceRecharge)
    }

    // 保留会员卡和礼品卡的方案
    const newPayMethods = [...filter(this.payMethods, x => x.type === 'ThirdCouponPay'), ...filter(this.payMethods, x => x.type === 'MemberCard' || x.type === 'GiftCard')]
    // 重新计算三方支付的方案
    this._buildExternal(newPayMethods)

    this._setPayMethods(newPayMethods, [method])
  }

  setGiftCardPayAmount (giftCard: IMemberGiftCardInfo, amount: number) {
    const method = find(this.payMethods, x => x.type === 'GiftCard' && x.giftCard.giftcardId === giftCard.giftcardId && x.giftCard.memberCardId === giftCard.memberCardId)
    // this.userGiftCardCardPayAmount[giftCard.cardNo] = amount
    this.userChangedPlan = true

    if (method) {
      method.amount = amount
      // 新的金额不足以包含原有的小费金额
      if (method.tip > amount) {
        method.tip = amount
      }
      // 尽可能吸收其它方式支付的小费到当前支付方式
      if (giftCard.giftcard.isPayTip) {
        method.tip = Math.min(this.tipAmount, amount)
      }

      // 保留会员卡和礼品卡的方案
      const newPayMethods = [...filter(this.payMethods, x => x.type === 'ThirdCouponPay'), ...filter(this.payMethods, x => x.type === 'MemberCard' || x.type === 'GiftCard')]
      // 重新计算三方支付的方案
      this._buildExternal(newPayMethods)

      this._setPayMethods(newPayMethods, [method])
    } else {
      // 保留已有会员卡和礼品卡的方案
      const newPayMethods = [...without(this.payMethods, this.externalMethod)]

      let tip = 0
      // 尽可能吸收其它方式支付的小费到当前支付方式
      if (giftCard.giftcard.isPayTip) {
        tip = Math.min(this.tipAmount, amount)
      }

      const method: IGiftCardPayMethod = {
        type: 'GiftCard',
        channel: 'GiftCard',
        giftCard,
        amount,
        tip,
      }
      newPayMethods.push(method)
      if (this.externalMethod) {
        newPayMethods.push(this.externalMethod)
      }

      this._setPayMethods(newPayMethods, [method])
    }
  }

  updateOrder (order: Partial<IPayingOrder>) {
    if (this.cacheOrder) {
      Object.assign(this.cacheOrder, order)
      console.toJS('保存订单支付方案', this.cacheOrder)
    }
  }

  setMethodCardInfo (cardInfo: ICardConnectApplePayCardInfo | IGlobalPaymentsApplePayCardInfo | IStripeApplePayCardInfo) {
    const method = find(this.payMethods, x => x.type === 'ApplePayWeb') as (ICardConnectApplePayWebPayMethod | IGlobalPaymentsApplePayWebPayMethod | IStripeApplePayWebPayMethod)
    if (method) {
      const newMethods = without(this.payMethods, method)
      method.cardInfo = cardInfo
      newMethods.push(method)
      this.payMethods = newMethods
    }
  }

  addCacheGiftCardShowList (list: IMemberGiftCardInfo[]) {
    // let _cacheList = toJS(this.cacheGiftCardShowList)?.filter(cache => !list?.find(item => item?.cardNo === cache?.cardNo))
    let json = [...list]
    let newJson = []
    for (let item1 of json) {
      let flag = true
      for (let item2 of newJson) {
        if (item1.cardNo === item2.cardNo) {
          flag = false
        }
      }
      if (flag) {
        newJson.push(item1)
      }
    }
    this.cacheGiftCardShowList = newJson?.sort((a, b) => (a.balance || 0) - (b.balance || 0))
  }

  setShowAllGiftCardList (all: boolean) {
    this.showAllGiftCardList = all
  }
}

const payPlanMobx = new PayPlan()

autorun(async () => {
  const { ordersId, info, cartResult } = cartMobx
  const { eatType } = info
  const { totalPrice, tax, serviceCharge } = cartResult
  const order: IPayingOrder = {
    type: 'dining',
    uniqueKey: info.payMode ? (ordersId || 'temp') : 'temp',
    diningOption: ['pickup'].includes(eatType) ? 'togo' : eatType,
    subtotal: toDecimal(totalPrice - tax - serviceCharge, 2) as any,
    tax: tax,
    extraFee: serviceCharge,
  }
  const memberCard = memberMobx.cardInfoForPay
  const memberGiftCards = memberMobx.giftCardListForPay?.filter(a => a.balance)
  if (payPlanMobx.shouldCalc) {
    payPlanMobx.calc(order, memberCard, memberGiftCards, thirdPartyMobx.couponResult.couponArr)
    payPlanMobx.addCacheGiftCardShowList(memberGiftCards)
  }
})

;(window as any).payPlanMobx = payPlanMobx

export default payPlanMobx
